import { DEFAULT_RWD_SELECTORS, detectRwdStage } from './rwdSelectors'

function normalizeId(value) {
  const text = String(value ?? '').trim()
  return /^[1-9]\d*$/.test(text) && Number.isSafeInteger(Number(text)) ? Number(text) : null
}

function readControl(root, selector, attribute = 'value') {
  if (!root) {
    return { present: false, id: null }
  }
  const controls = [...root.querySelectorAll(selector)]
  const selected = controls.find(control => !control.disabled && (
    !['radio', 'checkbox'].includes(control.type) || control.checked
  ))
  const value = attribute === 'value' ? selected?.value : selected?.getAttribute(attribute)
  return { present: controls.length > 0, id: normalizeId(value) }
}

export function readRwdDomState(
  root = document,
  selectors = DEFAULT_RWD_SELECTORS,
  { scopeShippingToList = false } = {},
) {
  const stage = detectRwdStage(root, selectors)
  const shippingRoot = scopeShippingToList && ['basket', 'purchase-choice'].includes(stage)
    ? root.querySelector(selectors.shippingList)
    : root
  const shipping = readControl(shippingRoot, selectors.shippingControl, selectors.shippingIdAttribute)
  const payment = readControl(root, selectors.paymentControl)
  const differentAddress = Boolean(root.querySelector(selectors.differentAddress)?.checked)
  const countrySelector = stage === 'address'
    ? (differentAddress ? selectors.deliveryCountry : selectors.addressCountry)
    : selectors.basketCountry
  const country = readControl(root, countrySelector)
  const codInput = root.querySelector(selectors.codInput)
  return {
    stage,
    shippingId: shipping.id,
    paymentId: payment.id,
    countryId: country.id,
    shippingControlPresent: shipping.present,
    paymentControlPresent: payment.present,
    countryControlPresent: country.present,
    differentAddress,
    // Pusty hidden to potwierdzony sygnał braku COD na formularzu adresowym.
    isCod: codInput?.value === '' ? false : codInput?.value === '1' ? true : null,
  }
}

export class RwdStateError extends Error {
  constructor(code, message) {
    super(message)
    this.name = 'RwdStateError'
    this.code = code
  }
}

export function requestRwdFrontApi(frontApi, method, params = {}, timeoutMs = 8000) {
  return new Promise((resolve, reject) => {
    if (typeof frontApi?.[method] !== 'function') {
      reject(new RwdStateError('api-unavailable', 'Nie można odczytać danych koszyka Shopera.'))
      return
    }
    let settled = false
    const finish = (error, value) => {
      if (settled) return
      settled = true
      clearTimeout(timer)
      if (error) reject(error)
      else resolve(value)
    }
    const timer = setTimeout(() => finish(new RwdStateError(
      'api-timeout', 'Odczyt danych koszyka Shopera trwa zbyt długo.',
    )), timeoutMs)
    try {
      frontApi[method](value => {
        if (!value || typeof value !== 'object' || Array.isArray(value)) {
          finish(new RwdStateError('api-invalid-response', 'Shoper zwrócił nieprawidłowe dane koszyka.'))
        } else {
          finish(null, value)
        }
      }, params)
    } catch {
      finish(new RwdStateError('api-read-failed', 'Nie udało się odczytać danych koszyka Shopera.'))
    }
  })
}

export async function getRwdBasket(frontApi, timeoutMs) {
  const basket = await requestRwdFrontApi(frontApi, 'getBasketInfo', {}, timeoutMs)
  if (!basket.basket || !Number.isFinite(basket.basket.count) || basket.basket.count < 0
    || !Array.isArray(basket.products)
    || (basket.basket.count === 0) !== (basket.products.length === 0)) {
    throw new RwdStateError('basket-invalid', 'Nie można potwierdzić aktualnego stanu koszyka.')
  }
  return basket
}

export async function getRwdCountry(frontApi, countryId, timeoutMs) {
  const id = normalizeId(countryId)
  if (!id) throw new RwdStateError('country-missing', 'Nie wybrano kraju dostawy.')
  // getCountry potwierdzono w RWD; dokumentacja opisuje też getCountries({id}).
  const method = typeof frontApi?.getCountry === 'function' ? 'getCountry' : 'getCountries'
  const country = await requestRwdFrontApi(frontApi, method, { id }, timeoutMs)
  const iso = typeof country.isocode === 'string' ? country.isocode.trim().toUpperCase() : ''
  if (normalizeId(country.country_id) !== id || !/^[A-Z]{2}$/.test(iso)) {
    throw new RwdStateError('country-unresolved', 'Nie można ustalić kraju dostawy.')
  }
  return iso
}

export function getRwdPayment(basket, paymentId) {
  const id = normalizeId(paymentId)
  if (!id) throw new RwdStateError('payment-missing', 'Nie wybrano sposobu płatności.')
  const payment = Array.isArray(basket.payments)
    ? basket.payments.find(item => normalizeId(item.payment_id) === id) : null
  const type = typeof payment?.name === 'string' ? payment.name.trim().toLowerCase() : ''
  if (!type) throw new RwdStateError('payment-unresolved', 'Nie można ustalić typu płatności.')
  return type
}

function selectedId(items, key) {
  if (!Array.isArray(items)) return null
  const selected = items.filter(item => item.selected === true)
  return selected.length === 1 ? normalizeId(selected[0][key]) : null
}

export function createRwdCartKey(basket) {
  const count = Number(basket?.basket?.count)
  if (!Number.isFinite(count) || !Array.isArray(basket?.products) || basket.products.length === 0) return null
  const products = basket.products.map((product) => {
    const productId = String(product?.product_id ?? product?.id ?? '').trim()
    const variantId = String(product?.stock_id ?? product?.variant_id ?? product?.variant ?? '').trim()
    const quantity = Number(product?.quantity ?? product?.qty ?? product?.count)
    if (productId === '' || !Number.isFinite(quantity) || quantity <= 0) return null
    return [productId, variantId, quantity]
  })
  if (products.some(product => product === null)) return null
  products.sort((left, right) => JSON.stringify(left).localeCompare(JSON.stringify(right)))
  return JSON.stringify([count, products])
}

export function createRwdStateReader({
  root = document,
  selectors = DEFAULT_RWD_SELECTORS,
  frontApi = () => window.frontAPI,
  timeoutMs = 8000,
  scopeShippingToList = false,
  prepareAddress = () => {},
} = {}) {
  let revision = 0
  const invalidate = () => ++revision

  return {
    invalidate,
    getRevision: () => revision,
    async read() {
      const token = invalidate()
      let initialDom = readRwdDomState(root, selectors, { scopeShippingToList })
      const isCurrent = () => token === revision
        && JSON.stringify(initialDom) === JSON.stringify(readRwdDomState(root, selectors, { scopeShippingToList }))
      try {
        const api = typeof frontApi === 'function' ? frontApi() : frontApi
        const basket = await getRwdBasket(api, timeoutMs)
        if (!isCurrent()) return null
        const empty = basket.basket.count === 0 && basket.products.length === 0
        if (empty) {
          return { revision: token, domState: initialDom, stage: initialDom.stage, empty: true, shippingId: null,
            countryIso: null, paymentType: null, cartKey: null, basket }
        }
        const shippingId = initialDom.shippingControlPresent || initialDom.stage === 'basket'
          ? initialDom.shippingId : selectedId(basket.shippings, 'shipping_id')
        prepareAddress({ stage: initialDom.stage, shippingId })
        initialDom = readRwdDomState(root, selectors, { scopeShippingToList })
        const paymentId = initialDom.paymentControlPresent || initialDom.stage === 'basket'
          ? initialDom.paymentId : selectedId(basket.payments, 'payment_id')
        // Na formularzu wymagamy właściwej kontrolki; brak country2 przy different
        // nie może zostać zamaskowany starym adresem zapisanym na serwerze.
        const countryId = initialDom.countryControlPresent || ['address', 'basket'].includes(initialDom.stage)
          ? initialDom.countryId : normalizeId(basket.shipping_address?.country_id)
        if (!shippingId) throw new RwdStateError('shipping-missing', 'Nie można ustalić metody dostawy.')
        const paymentType = getRwdPayment(basket, paymentId)
        const countryIso = await getRwdCountry(api, countryId, timeoutMs)
        if (!isCurrent()) return null
        return { revision: token, domState: initialDom, stage: initialDom.stage, empty: false, shippingId,
          countryId, countryIso, paymentId, paymentType, cartKey: createRwdCartKey(basket), basket }
      } catch (error) {
        if (!isCurrent()) return null
        throw error
      }
    },
  }
}
